import { useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery, Checkbox, Grid } from "@mui/material";
import { useShallow } from "zustand/react/shallow";
import clsx from "clsx";
import agent from "../../../services/api";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import { useGenericStore } from "../../../store/genericStore";
import { useTicketStore } from "../../../store/ticketStore";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { IntegrationContext, IntegrationContextPayload } from "../../../context/integrationContext";
import { DEFAULT_PAGE_LIMIT, DEFAULT_START_FROM } from "../constants";
import {
    hasAssignedAgent,
    isChannelNameChronoxAiWebApp,
    isChannelNameChronoxAiWebAppOrWebsite,
    isChannelNameWhatsApp,
    isConversationHandleByOtherAgent,
    isDefaultActionType,
    isHideChatActionMenuItem,
    isOnlySmbUserAssigned,
    isThreadArchived,
    isUserChatExpired,
    shouldShowAssignAgentOption,
    shouldShowCreateTicketOption,
    updateChatbotUserIdParam,
} from "../utils";
import {
    currentActiveChatbotThreadTypeNew,
    currentActiveThreadChatbotThreadData,
    globalMenuItemData,
} from "../../../models/axiosModel";
import { CHRONOX_FEATURES, hasInboxReadAccess, isAdmin, isFeatureDisabled } from "../../../utils/constants";
import { getThreadTicketDisableFeatureTooltipTitle } from "../renderUtils";
import { THREAD_STATUS } from "../threadInfo/components/threadStatus/customerConversationAssignedStatus";
import GlobalMenu from "../../../components/globalMenu";
import styles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import { isInstagramChannel } from "../channelConstants";

/** @deprecated Prefer globalMenuItemData; kept for isHideChatActionMenuItem callers */
export interface menuItems {
    title: string;
    onclick: () => void;
}

interface UseInboxThreadMenuActionsParams {
    getSpecificChat: (
        chatbot_user_id: number | string,
        offSetValue: number,
        flagDebugParam: boolean,
        flagViewDetailedLogParam: boolean,
        forceRefresh?: boolean
    ) => void | Promise<void>;
    handleToggleManualOverride: (user_assignment_id: string, flag_delete_agent: boolean) => void;
    getManualOverrideConfig: (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => string;
    flagRuleBasedInbox?: boolean;
    onArchiveToggle?: (threadId: string, flagArchive: boolean) => void;
}

/**
 * Chat-level menu actions and GlobalMenu for the thread log view.
 */
export const useInboxThreadMenuActions = ({
    getSpecificChat,
    handleToggleManualOverride,
    getManualOverrideConfig,
    flagRuleBasedInbox,
    onArchiveToggle,
}: UseInboxThreadMenuActionsParams) => {
    const { setFlagLoading, handleError, userDetail, vendorData, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;
    const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
    const [, setSearchParams] = useSearchParams();
    const { showSuccess } = useGenericStore(
        useShallow((state) => ({
            showSuccess: state.showSuccess,
        }))
    );
    const { setSelectedTicket, setFlagCreateTicketPopup } = useTicketStore(
        useShallow((state) => ({
            setSelectedTicket: state.setSelectedTicket,
            setFlagCreateTicketPopup: state.setFlagCreateTicketPopup,
        }))
    );

    const mobileScreen = useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)');
    const mobileTabScreen = useMediaQuery<boolean>('(max-width : 1250px) and (min-width : 0px)');

    const {
        currentActiveChatbotThread,
        setCurrentActiveChatbotThread,
        setSourceDetails,
        chatLevelMenu,
        onCloseOfChatLevelMenu,
        flagDebug,
        setFlagDebug,
        flagViewDetailedLog,
        setFlagViewDetailedLog,
        flagNeedToShowDetailedLogBtn,
        setFlagAssignAgentPopup,
        setFlagRemoveAgentPopup,
        fetchThreads,
        setNewThreadInfo,
        newThreadInfo,
        subType,
        checkAgentActivityMsgEnabled,
        flagRenderNewConversation,
        setFlagRenderNewConversation,
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
            setSourceDetails: state.setSourceDetails,
            chatLevelMenu: state.chatLevelMenu,
            onCloseOfChatLevelMenu: state.onCloseOfChatLevelMenu,
            flagDebug: state.flagDebug,
            setFlagDebug: state.setFlagDebug,
            flagViewDetailedLog: state.flagViewDetailedLog,
            setFlagViewDetailedLog: state.setFlagViewDetailedLog,
            flagNeedToShowDetailedLogBtn: state.flagNeedToShowDetailedLogBtn,
            setFlagAssignAgentPopup: state.setFlagAssignAgentPopup,
            setFlagRemoveAgentPopup: state.setFlagRemoveAgentPopup,
            fetchThreads: state.fetchThreads,
            setNewThreadInfo: state.setNewThreadInfo,
            newThreadInfo: state.newThreadInfo,
            subType: state.subType,
            checkAgentActivityMsgEnabled: state.checkAgentActivityMsgEnabled,
            flagRenderNewConversation: state.flagRenderNewConversation,
            setFlagRenderNewConversation: state.setFlagRenderNewConversation,
        }))
    ); 

    const channelName = currentActiveChatbotThread?.chatbot_user_route_source ?? "";
    const isInstagram = isInstagramChannel(channelName);

    const onClickOfAssignAgent = useCallback((flagAssignAgent: boolean) => {
        if (flagAssignAgent) {
            setFlagAssignAgentPopup(true);
        } else {
            setFlagRemoveAgentPopup(true);
        }
        onCloseOfChatLevelMenu();
    }, [setFlagAssignAgentPopup, setFlagRemoveAgentPopup, onCloseOfChatLevelMenu]);

    const handleDeleteThread = useCallback(async () => {
        if (!currentActiveChatbotThread) return;
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        const chatbot_user_id = "chatbot_user_id" in currentActiveChatbotThread ? currentActiveChatbotThread.chatbot_user_id : "";
        if (chatbot_user_id) {
            const res = await agent.deleteThread.delete(chatbot_user_id);
            if (res?.status) {
                if(flagRenderNewConversation){
                    setFlagRenderNewConversation(false);
                }
                if (newThreadInfo) {
                    setNewThreadInfo(null);
                }
                setCurrentActiveChatbotThread(null);
                setSourceDetails({});
                setSearchParams(updateChatbotUserIdParam(null));
                showSuccess("Chat deleted successfully!")
                onCloseOfChatLevelMenu();
                await fetchThreads(DEFAULT_START_FROM, DEFAULT_PAGE_LIMIT);
            } else {
                handleError(res);
            }
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }, [
        currentActiveChatbotThread,
        setFlagLoading,
        newThreadInfo,
        setNewThreadInfo,
        flagRenderNewConversation,
        setFlagRenderNewConversation,
        setCurrentActiveChatbotThread,
        setSourceDetails,
        setSearchParams,
        showSuccess,
        onCloseOfChatLevelMenu,
        fetchThreads,
        handleError,
    ]);

    const closeStaticWorkflowThread = useCallback(async () => {
        if (currentActiveChatbotThread) {
            onCloseOfChatLevelMenu();
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
            const thread_id = currentActiveChatbotThread?.thread_id;
            const res = await agent.closeStaticWorkflowThread.post(thread_id);
            if (!res.status) {
                handleError(res);
            } else {
                showSuccess("Chat closed successfully!")
                await getSpecificChat(currentActiveChatbotThread.chatbot_user_id, 0, flagDebug, flagViewDetailedLog);
            }
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
        }
    }, [
        currentActiveChatbotThread,
        onCloseOfChatLevelMenu,
        setFlagLoading,
        handleError,
        showSuccess,
        getSpecificChat,
        flagDebug,
        flagViewDetailedLog,
    ]);

    const isMenuItemDisabled = useCallback((title: string) => {
        if (isThreadArchived(currentActiveChatbotThread) && title?.toLowerCase() == "assign agent") {
            return true;
        }
        return false;
    }, [subType, currentActiveChatbotThread]);

    const shouldShowActionBtn = useCallback((flagInsideMenuItem: boolean, condition: boolean) => {
        if (flagInsideMenuItem) return condition;
        return !condition;
    }, []);

    const checkDisabledManualOverRideBtn = useCallback(() => {
        if (isChannelNameChronoxAiWebApp(currentActiveChatbotThread)) return true;

        if (getManualOverrideConfig(currentActiveChatbotThread)?.toLowerCase() == "takeover") {
            return (hasAssignedAgent(currentActiveChatbotThread) && !isOnlySmbUserAssigned(currentActiveChatbotThread)) 
                    || isThreadArchived(currentActiveChatbotThread)
                    || (isInstagram && isUserChatExpired(currentActiveChatbotThread));
        }
        return false;
    }, [currentActiveChatbotThread, getManualOverrideConfig]);

    const shouldShowManualOverrideBtn = useCallback((flagInsideMenuItem: boolean) => {
        if (currentActiveChatbotThread && currentActiveChatbotThread.chatbot_user_active_flag && hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail)) {
            return shouldShowActionBtn(flagInsideMenuItem, flagRuleBasedInbox ? mobileTabScreen && !mobileScreen : mobileTabScreen);
        }
        return false;
    }, [currentActiveChatbotThread, apiRoutesPermissionAssignment, userDetail, shouldShowActionBtn, flagRuleBasedInbox, mobileTabScreen, mobileScreen]);

    const shouldShowArchiveActionBtn = useCallback((flagInsideMenuItem: boolean) => {
        if (flagRuleBasedInbox && currentActiveChatbotThread && hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail) && !isChannelNameChronoxAiWebAppOrWebsite(currentActiveChatbotThread)) {
            return shouldShowActionBtn(flagInsideMenuItem, mobileTabScreen);
        }
        return false;
    }, [flagRuleBasedInbox, currentActiveChatbotThread, apiRoutesPermissionAssignment, userDetail, shouldShowActionBtn, mobileTabScreen]);

    const shouldShowDebuggingBtn = useCallback(() => {
        return Boolean(isAdmin(userDetail) && currentActiveChatbotThread && isChannelNameWhatsApp(currentActiveChatbotThread));
    }, [userDetail, currentActiveChatbotThread]);

    const shouldShowViewDetailedLogBtn = useCallback(() => {
        return Boolean(flagNeedToShowDetailedLogBtn && currentActiveChatbotThread && !isChannelNameChronoxAiWebAppOrWebsite(currentActiveChatbotThread));
    }, [flagNeedToShowDetailedLogBtn, currentActiveChatbotThread]);

    const handleDebuggingFlag = useCallback((value: boolean) => {
        if (currentActiveChatbotThread) {
            setFlagDebug(value);
            if (value) {
                getSpecificChat(currentActiveChatbotThread.thread_id, 0, value, flagViewDetailedLog);
            }
        }
    }, [currentActiveChatbotThread, setFlagDebug, getSpecificChat, flagViewDetailedLog]);

    const handleViewDetailedLogFlag = useCallback((value: boolean) => {
        setFlagViewDetailedLog(value);

        if (currentActiveChatbotThread) {
            const updatedCurrentActiveThread: currentActiveChatbotThreadTypeNew = {
                ...currentActiveChatbotThread,
                chatbot_thread_data: currentActiveChatbotThread?.chatbot_thread_data?.map((thread: currentActiveThreadChatbotThreadData) => {
                    return {
                        ...thread,
                        flag_show_action: value ? true : isDefaultActionType(thread?.action_type_name ?? ""),
                    }
                })
            };
            setCurrentActiveChatbotThread(updatedCurrentActiveThread);
        }
    }, [currentActiveChatbotThread, setFlagViewDetailedLog, setCurrentActiveChatbotThread]);

    const onClickOfCreateTicket = useCallback(() => {
        setFlagCreateTicketPopup(true);
        setSelectedTicket(null);
        onCloseOfChatLevelMenu();
    }, [setFlagCreateTicketPopup, setSelectedTicket, onCloseOfChatLevelMenu]);

    const onClickOfViewDetailedLogMenu = useCallback((value: boolean) => {
        handleViewDetailedLogFlag(value);
        onCloseOfChatLevelMenu();
    }, [handleViewDetailedLogFlag, onCloseOfChatLevelMenu]);

    const onClickOfFlagDebuggingMenu = useCallback((value: boolean) => {
        handleDebuggingFlag(value);
        onCloseOfChatLevelMenu();
    }, [handleDebuggingFlag, onCloseOfChatLevelMenu]);

    const ensureNoAgentIsAssigned = useCallback(async (): Promise<boolean> => {
        if (!currentActiveChatbotThread) return false;

        if (!hasAssignedAgent(currentActiveChatbotThread)) return true;

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        try {
            const response = await agent.removeManualAgent.delete(
                currentActiveChatbotThread?.manual_agent_data?.[0]?.thread_manual_agent_id,
                checkAgentActivityMsgEnabled(integrationListNewStructure)
            );
            if (!response.status) {
                handleError(response);
                return false;
            }
            return true;
        } finally {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    }, [currentActiveChatbotThread, setFlagLoading, checkAgentActivityMsgEnabled, integrationListNewStructure, handleError]);

    const getArchiveActionState = useCallback(() => {
        const threadData = currentActiveChatbotThread as currentActiveChatbotThreadTypeNew;

        const hasOtherAgentAssigned = isConversationHandleByOtherAgent(currentActiveChatbotThread, userDetail?.user_assignment_id);

        const threadTicketData = threadData?.thread_ticket_data;
        const hasOpenTicketStatus = threadTicketData?.some((ticket) => ticket?.status_assignments?.some((status) => status?.ticket_status_name == THREAD_STATUS['OPEN']));

        const hasUnresolvedTicket = !!(
            threadTicketData &&
            threadTicketData?.length > 0 &&
            hasOpenTicketStatus
        );

        const isArchiveDisabled = !threadData?.flag_archive && (hasOtherAgentAssigned || hasUnresolvedTicket);

        const archiveInfoTooltip =
            `You can mark a chat as Archive if you no longer want to view it in the Active View. Archived chats are not deleted and can be accessed anytime.
            ${isInstagram ? " Archived chats will remain archived until you choose to unarchive them."
                : " When a new message is received in an archived chat, it will automatically become Unarchived and reappear in the Active View."}`;

        const getArchiveTooltip = () => {
            if (isArchiveDisabled) {
                if (hasOtherAgentAssigned && hasUnresolvedTicket) {
                    return "Cannot archive: A manual agent is assigned and there is an unresolved ticket";
                } else if (hasOtherAgentAssigned) {
                    return "Cannot archive: A manual agent is assigned to this thread";
                } else if (hasUnresolvedTicket) {
                    return "Cannot archive: There is an unresolved ticket for this thread";
                }
            }
            return archiveInfoTooltip;
        };

        const handleArchiveToggle = async () => {
            const flagNoAgentIsAssigned = await ensureNoAgentIsAssigned();
            if (!flagNoAgentIsAssigned) return;

            const threadId = threadData?.thread_id;
            const currentArchiveStatus = threadData?.flag_archive ?? false;

            if (threadId && onArchiveToggle) {
                onArchiveToggle(threadId, !currentArchiveStatus);
            }
        };

        return {
            threadData,
            isArchiveDisabled,
            archiveTooltip: getArchiveTooltip(),
            handleArchiveToggle,
            buttonText: threadData?.flag_archive ? "Unarchive" : "Archive"
        };
    }, [currentActiveChatbotThread, userDetail?.user_assignment_id, ensureNoAgentIsAssigned, onArchiveToggle]);

    const renderCheckboxMenuLabel = useCallback((label: string, checked: boolean) => (
        <Grid item className={styles.flagDebuggingGrid}>
            <Grid container alignItems={"center"} gap={"5px"}>
                <Grid item className={styles.debugCheckBoxGridForMenuItem}>
                    <Checkbox
                        className={`${styles.debugCheckBoxForMenuItem}`}
                        disableRipple
                        checked={checked}
                    />
                </Grid>
                <Grid item className={globalStyles.blackColor}>
                    {label}
                </Grid>
            </Grid>
        </Grid>
    ), []);

    const chatLevelMenuItemData: globalMenuItemData[] = useMemo(() => {
        const menuItemClassName = clsx("menu-message-item", mobileScreen ? globalStyles.secondaryFontSize : globalStyles.primaryFontSize);
        const items: globalMenuItemData[] = [];

        if (isChannelNameWhatsApp(currentActiveChatbotThread) || isInstagramChannel(currentActiveChatbotThread?.channel_name)) {
            items.push({
                type: "item",
                label: "Close",
                onClick: () => closeStaticWorkflowThread(),
            });
        }

        items.push({
            type: "item",
            label: "Delete",
            onClick: () => handleDeleteThread(),
        });

        if (shouldShowAssignAgentOption(currentActiveChatbotThread)) {
            items.push({
                type: "item",
                label: 'Assign Agent',
                onClick: () => onClickOfAssignAgent(true),
                isDisable: isMenuItemDisabled('Assign Agent'),
                ...(isThreadArchived(currentActiveChatbotThread) ? {
                    tooltip: 'Cannot assign: thread is archived',
                    tooltipPlacement: 'left' as const,
                } : {}),
            });
        }

        if (hasAssignedAgent(currentActiveChatbotThread)) {
            items.push({
                type: "item",
                label: 'Remove Agent',
                onClick: () => onClickOfAssignAgent(false),
            });
        }

        if (shouldShowCreateTicketOption(apiRoutesPermissionAssignment, userDetail)) {
            const createTicketTooltip = getThreadTicketDisableFeatureTooltipTitle(userDetail, vendorData);
            items.push({
                type: "item",
                label: "Create Ticket",
                onClick: () => onClickOfCreateTicket(),
                isDisable: isFeatureDisabled(userDetail, CHRONOX_FEATURES['THREAD_TICKETING']),
                ...(createTicketTooltip ? {
                    tooltip: createTicketTooltip,
                    tooltipPlacement: 'left' as const,
                    tooltipArrow: true,
                } : {}),
            });
        }

        if (shouldShowManualOverrideBtn(true)) {
            items.push({
                type: "item",
                label: getManualOverrideConfig(currentActiveChatbotThread),
                isDisable: checkDisabledManualOverRideBtn(),
                onClick: () => {
                    handleToggleManualOverride(String(userDetail.user_assignment_id), getManualOverrideConfig(currentActiveChatbotThread) == "Leave");
                    onCloseOfChatLevelMenu();
                },
            });
        }

        if (shouldShowArchiveActionBtn(true)) {
            const {
                isArchiveDisabled,
                archiveTooltip,
                handleArchiveToggle,
                buttonText,
            } = getArchiveActionState();

            items.push({
                type: "item",
                label: buttonText,
                isDisable: isArchiveDisabled,
                tooltip: archiveTooltip,
                tooltipPlacement: 'left',
                onClick: () => {
                    handleArchiveToggle();
                    onCloseOfChatLevelMenu();
                },
            });
        }

        if (shouldShowViewDetailedLogBtn()) {
            items.push({
                type: "item",
                label: "View Detailed Log",
                content: renderCheckboxMenuLabel("View Detailed Log", flagViewDetailedLog),
                onClick: () => onClickOfViewDetailedLogMenu(!flagViewDetailedLog),
            });
        }

        if (shouldShowDebuggingBtn()) {
            items.push({
                type: "item",
                label: "Debugging",
                content: renderCheckboxMenuLabel("Debugging", flagDebug),
                onClick: () => onClickOfFlagDebuggingMenu(!flagDebug),
            });
        }

        return items
            .filter((item) => {
                const title = item.label ?? '';
                return !isHideChatActionMenuItem({ title }, userDetail, apiRoutesPermissionAssignment);
            })
            .map((item) => ({
                ...item,
                className: clsx(item.className, menuItemClassName),
            }));
    }, [
        mobileScreen,
        currentActiveChatbotThread,
        closeStaticWorkflowThread,
        handleDeleteThread,
        onClickOfAssignAgent,
        isMenuItemDisabled,
        apiRoutesPermissionAssignment,
        userDetail,
        vendorData,
        onClickOfCreateTicket,
        shouldShowManualOverrideBtn,
        getManualOverrideConfig,
        checkDisabledManualOverRideBtn,
        handleToggleManualOverride,
        onCloseOfChatLevelMenu,
        shouldShowArchiveActionBtn,
        getArchiveActionState,
        shouldShowViewDetailedLogBtn,
        renderCheckboxMenuLabel,
        flagViewDetailedLog,
        onClickOfViewDetailedLogMenu,
        shouldShowDebuggingBtn,
        flagDebug,
        onClickOfFlagDebuggingMenu,
    ]);

    const renderChatLevelMenu = useCallback(() => (
        <Grid item>
            <GlobalMenu
                MenuListProps={{
                    'aria-labelledby': 'long-button',
                }}
                className={mobileScreen ? styles.chatLevelMenuListMobile : ""}
                anchorEl={chatLevelMenu.anchorEl ?? null}
                open={chatLevelMenu.flagOpen ?? false}
                handleMenuClose={onCloseOfChatLevelMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                classes={{
                    paper: styles.chatLevelMenuPaper
                }}
                disablePortal
                menuItemData={chatLevelMenuItemData}
            />
        </Grid>
    ), [mobileScreen, chatLevelMenu.anchorEl, chatLevelMenu.flagOpen, onCloseOfChatLevelMenu, chatLevelMenuItemData]);

    return {
        renderChatLevelMenu,
        shouldShowManualOverrideBtn,
        shouldShowArchiveActionBtn,
        checkDisabledManualOverRideBtn,
        getArchiveActionState,
        onClickOfAssignAgent,
        handleDeleteThread,
        closeStaticWorkflowThread,
        isMenuItemDisabled,
    };
};
